import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Send } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import LineItemsSection, { newLineItem } from "@/components/LineItemsSection";

const REF_TABS = [
  { key: "po_number", label: "PO #" },
  { key: "invoice_number", label: "Invoice #" },
  { key: "job_number", label: "Job #" },
  { key: "customer_reference", label: "Customer Ref" },
  { key: "internal_reference", label: "Internal Ref" },
];

function F({ label, v, on, type = "text", testid, required, placeholder, mono }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">
        {label}{required && " *"}
      </Label>
      <Input
        type={type}
        data-testid={testid}
        value={v ?? ""}
        onChange={(e) => on(e.target.value)}
        required={required}
        placeholder={placeholder}
        className={`h-9 rounded-none border-2 focus-visible:ring-orange-500 ${mono ? "font-mono text-xs" : ""}`}
      />
    </div>
  );
}

function PartyBlock({ title, party, setParty, prefix }) {
  return (
    <div className="border-2 border-border p-4 space-y-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="Company" required v={party.company} on={(v) => setParty({ ...party, company: v })} testid={`edit-${prefix}-company`} />
        <F label="Contact" v={party.contact_name} on={(v) => setParty({ ...party, contact_name: v })} testid={`edit-${prefix}-contact`} />
        <F label="Email · comma-separate for multiple" v={party.email} on={(v) => setParty({ ...party, email: v })} testid={`edit-${prefix}-email`} placeholder="ops@x.com, dispatch@x.com" mono />
        <F label="Phone" v={party.phone} on={(v) => setParty({ ...party, phone: v })} testid={`edit-${prefix}-phone`} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Address *</Label>
        <Textarea
          data-testid={`edit-${prefix}-address`}
          value={party.address ?? ""}
          onChange={(e) => setParty({ ...party, address: e.target.value })}
          required
          rows={2}
          className="rounded-none border-2"
        />
      </div>
    </div>
  );
}

export default function EditRequestDialog({ open, onOpenChange, request, onSaved }) {
  const [type, setType] = useState("pickup");
  const [priority, setPriority] = useState("standard");
  const [pickup, setPickup] = useState({ company: "", contact_name: "", email: "", phone: "", address: "" });
  const [delivery, setDelivery] = useState({ company: "", contact_name: "", email: "", phone: "", address: "" });
  const [courier, setCourier] = useState({ courier_id: null, company: "", email: "" });
  const [references, setReferences] = useState({ po_number: "", invoice_number: "", job_number: "", customer_reference: "", internal_reference: "" });
  const [items, setItems] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!request) return;
    setType(request.request_type);
    setPriority(request.priority || "standard");
    setPickup({ ...request.pickup });
    setDelivery({ ...request.delivery });
    setCourier({ courier_id: request.courier?.courier_id || null, company: request.courier?.company || "", email: request.courier?.email || "" });
    setReferences({
      po_number: request.references?.po_number || "",
      invoice_number: request.references?.invoice_number || "",
      job_number: request.references?.job_number || "",
      customer_reference: request.references?.customer_reference || "",
      internal_reference: request.references?.internal_reference || "",
    });
    setItems((request.items || []).map((i, idx) => ({
      __key: `edit-${request.request_id || "r"}-${idx}`,
      item_number: i.item_number || "", description: i.description || "",
      quantity: i.quantity ?? 1,
      package_type: i.package_type || "",
      dimensions: i.dimensions || "",
      weight: i.weight ?? "",
      package_group: i.package_group ? String(i.package_group) : "0",
    })));
    setInstructions(request.instructions || "");
    // request prop drives the whole reset; setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const addItem = () => setItems([...items, newLineItem()]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const doSave = async (transmit) => {
    setSaving(true);
    try {
      const payload = {
        request_type: type,
        priority,
        pickup,
        delivery,
        courier: {
          courier_id: courier.courier_id || null,
          company: courier.company,
          email: courier.email,
        },
        references,
        items: items
          .filter((i) => i.description)
          .map((i) => ({
            item_number: i.item_number || "",
            description: i.description,
            quantity: Number(i.quantity) || 0,
            package_type: i.package_type || "",
            dimensions: i.dimensions || "",
            weight: i.weight === "" || i.weight == null ? null : Number(i.weight),
            package_group: i.package_group && i.package_group !== "0" ? parseInt(i.package_group, 10) : null,
          })),
        instructions,
        transmit,
      };
      await api.put(`/requests/${request.request_id}`, payload);
      if (transmit) {
        toast.success("Saved & transmitted", { description: "Supplier, courier and requestor were notified of the update" });
      } else {
        toast.success("Saved silently", { description: "No emails were sent for this update" });
      }
      setConfirmOpen(false);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const askTransmit = (e) => {
    e.preventDefault();
    setConfirmOpen(true);
  };

  if (!request) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="edit-request-dialog"
          className="rounded-none border-2 max-w-4xl max-h-[92vh] overflow-y-auto p-0"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b-2 border-border sticky top-0 bg-background z-10">
            <div className="flex items-center gap-3">
              <div className="border-2 border-foreground p-1.5 shrink-0">
                <LogoMark size={32} className="text-foreground" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-orange-600">
                  Out <span className="text-foreground">&amp;</span> In &middot; Edit Request
                </div>
                <DialogTitle className="font-heading text-2xl font-black tracking-tight font-mono">
                  {request.reference_number}
                </DialogTitle>
              </div>
            </div>
            <DialogDescription className="text-xs pt-2">
              Update details below. Choose "Save &amp; Transmit" to email the supplier, courier and requestor of the changes,
              or "Save Only" to update silently without notifying anyone.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={askTransmit} className="px-6 py-4 space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="edit-type" className="rounded-none h-9 border-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pickup">Pickup</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="edit-priority" className="rounded-none h-9 border-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="same_day">Same Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PartyBlock title="Pickup From" party={pickup} setParty={setPickup} prefix="pickup" />
              <PartyBlock title="Deliver To" party={delivery} setParty={setDelivery} prefix="delivery" />
            </div>

            <div className="border-2 border-border p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Courier</div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Company" required v={courier.company} on={(v) => setCourier({ ...courier, courier_id: null, company: v })} testid="edit-courier-company" />
                <F label="Email · comma-separate for multiple" required v={courier.email} on={(v) => setCourier({ ...courier, courier_id: null, email: v })} testid="edit-courier-email" placeholder="ops@courier.com, dispatch@courier.com" mono />
              </div>
            </div>

            <div className="border-2 border-border p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">References</div>
              <Tabs defaultValue="po_number">
                <TabsList className="rounded-none bg-transparent border-b-2 border-border w-full justify-start p-0 h-auto">
                  {REF_TABS.map((t) => (
                    <TabsTrigger key={t.key} value={t.key} data-testid={`edit-ref-tab-${t.key}`}
                      className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold">
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {REF_TABS.map((t) => (
                  <TabsContent key={t.key} value={t.key} className="mt-3">
                    <Input
                      data-testid={`edit-ref-input-${t.key}`}
                      value={references[t.key] ?? ""}
                      onChange={(e) => setReferences({ ...references, [t.key]: e.target.value })}
                      placeholder={`Enter ${t.label}`}
                      className="h-9 rounded-none border-2"
                    />
                  </TabsContent>
                ))}
              </Tabs>
            </div>

            <LineItemsSection items={items} setItems={setItems} prefix="edit-li" />

            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Instructions</Label>
              <Textarea data-testid="edit-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2} className="rounded-none border-2" />
            </div>

            <DialogFooter className="pt-4 border-t-2 border-border sticky bottom-0 bg-background -mx-6 px-6 pb-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-cancel-button" className="rounded-none border-2 h-10 uppercase tracking-[0.15em] text-xs font-bold">
                Cancel
              </Button>
              <Button type="submit" data-testid="edit-save-button" className="rounded-none bg-orange-600 hover:bg-orange-700 h-10 uppercase tracking-[0.15em] text-xs font-bold">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="transmit-dialog" className="rounded-none border-2 max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="border-2 border-foreground p-1.5 shrink-0">
                <LogoMark size={28} className="text-foreground" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-orange-600">Choose how to save</div>
                <DialogTitle className="font-heading text-2xl font-black tracking-tight">
                  Transmit the update?
                </DialogTitle>
              </div>
            </div>
            <DialogDescription className="text-sm pt-2">
              Your changes to <span className="font-mono font-bold">{request.reference_number}</span> are ready to save.
              Do you want to notify the supplier, courier and requestor of the update, or save quietly without sending any email?
            </DialogDescription>
          </DialogHeader>
          <div className="border-2 border-border bg-muted p-3 text-xs text-muted-foreground">
            <div className="font-bold uppercase tracking-[0.15em] mb-1 text-foreground">Recipients if transmitted</div>
            <div>&bull; Supplier &mdash; {pickup.email || "no email"}</div>
            <div>&bull; Courier &mdash; {courier.email || "no email"}</div>
            <div>&bull; Requestor &mdash; {request.created_by_email || "current user"}</div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => doSave(false)}
              disabled={saving}
              data-testid="save-not-transmit-button"
              className="rounded-none border-2 h-11 uppercase tracking-[0.15em] text-xs font-bold flex-1"
            >
              <Save className="h-3.5 w-3.5 mr-2" />
              {saving ? "Saving..." : "Save Only"}
            </Button>
            <Button
              type="button"
              onClick={() => doSave(true)}
              disabled={saving}
              data-testid="save-and-transmit-button"
              className="rounded-none bg-orange-600 hover:bg-orange-700 h-11 uppercase tracking-[0.15em] text-xs font-bold flex-1"
            >
              <Send className="h-3.5 w-3.5 mr-2" />
              {saving ? "Sending..." : "Save & Transmit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
